import { PageInfo } from "@/components/Pagination";
import { TypedDocumentNode, gql } from "@apollo/client";
import { PAGE_INFO_FRAGMENT } from "@/hooks/usePagination";
import { OrderNode } from "@/pages/billing/types";

type Query = {
  duesOrders: {
    totalCount: number;
    nodes: OrderNode[];
    pageInfo: PageInfo;
  };
  totalDuesOrders: {
    totalCount: number;
  };
  totalAvailableDuesOrdersWithFailedPayments: {
    totalCount: number;
  };
  totalDuesOrdersWithFailedPayments: {
    totalCount: number;
  };
  totalDuesOverdueOrders: {
    totalCount: number;
  };
};

export const ORDERS_QUERY: TypedDocumentNode<Query> = gql`
  query GetDuesOrders(
    $first: Int
    $last: Int
    $after: String
    $before: String
    $availableForTerminations: Boolean
    $availableForSuspensions: Boolean
    $search: String
    $dueDateRange: DateRangeInput
    $ids: [String!]
    $balanceRange: MoneyCentsRangeInput
    $sectionIds: [String!]
    $memberClassifications: [String!]
    $facilityIds: [String!]
    $statuses: [CommerceOrderStatus!]
    $paymentStatuses: [CommerceOrderPaymentStatus!]
  ) {
    totalDuesOrders: duesOrders(
      filters: {
        availableForTerminations: $availableForTerminations
        availableForSuspensions: $availableForSuspensions
      }
    ) {
      totalCount
    }
    totalAvailableDuesOrdersWithFailedPayments: duesOrders(
      filters: {
        availableForTerminations: $availableForTerminations
        availableForSuspensions: $availableForSuspensions
        paymentStatuses: [HAS_FAILED_PAYMENTS]
      }
    ) {
      totalCount
    }
    totalDuesOrdersWithFailedPayments: duesOrders(
      filters: { statuses: [OPEN], paymentStatuses: [HAS_FAILED_PAYMENTS] }
    ) {
      totalCount
    }
    totalDuesOverdueOrders: duesOrders(filters: { statuses: [OPEN], paymentStatuses: [IS_OVERDUE] }) {
      totalCount
    }
    duesOrders(
      first: $first
      last: $last
      after: $after
      before: $before
      filters: {
        availableForTerminations: $availableForTerminations
        availableForSuspensions: $availableForSuspensions
        search: $search
        dueDateRange: $dueDateRange
        ids: $ids
        balanceRange: $balanceRange
        sectionIds: $sectionIds
        memberClassifications: $memberClassifications
        facilityIds: $facilityIds
        statuses: $statuses
        paymentStatuses: $paymentStatuses
      }
    ) {
      nodes {
        id
        status
        balance
        dueDate
        payable
        updatedAt
        description
        status
        paymentStatus
        person {
          id
          firstName
          lastName
          profilePhoto
          pgaId
          classification
          primarySectionAffiliation {
            id
            section {
              id
              name
            }
          }
          mainProgramType {
            id
            name
          }
        }
        lineItems {
          id
        }
      }
      ...PageInfo
    }
  }
  ${PAGE_INFO_FRAGMENT}
`;

export const CHANGE_DUE_DATES_MUTATION = gql`
  mutation ChangeDueDates($input: CommerceBulkChangeOrderDueDateInput!) {
    commerceBulkChangeOrderDueDate(input: $input) {
      successfulOrders {
        id
        dueDate
      }
      failedOrders {
        id
        dueDate
      }
      message
      success
    }
  }
`;

export const TERMINATE_PROGRAMS_MUTATION = gql`
  mutation terminatePrograms($input: BackofficeDuesBulkTerminateProgramsInput!) {
    duesBulkTerminatePrograms(input: $input) {
      successfulPersonIds
      failedPersonIds
      message
      success
    }
  }
`;

export const SUSPEND_PROGRAMS_MUTATION = gql`
  mutation suspendPrograms($input: BackofficeDuesBulkSuspendProgramsInput!) {
    duesBulkSuspendPrograms(input: $input) {
      successfulPersonIds
      failedPersonIds
      message
      success
    }
  }
`;
